/**
 * Pharmacy platform modules:
 * - pharmacyPortal  — pharmacy_admin / pharmacy_staff portal actions
 * - pharmacyAdmin   — super-admin pharmacy management
 * - pharmacyMedications — medication inventory
 */
import { fetchApi } from '../client';
import type {
  Pharmacy,
  CreatePharmacyUserRequest,
  CreatePharmacyUserResponse,
  PharmacyInvitation,
  PharmacyRegistrationRequest,
  PharmacyInviteRequest,
  PharmacySearchRequest,
  PharmacyStatistics,
  PharmacyUser,
  PharmacyPrescriptionDelivery,
  PharmacyPrescriptionStats,
  CreateInHousePharmacyRequest,
  InHousePharmacyResponse,
  InvitePharmacyStaffRequest,
  PharmacyStaffInvitationResponse,
} from '../../../types';

// ── Pharmacy Portal (pharmacy_admin / pharmacy_staff) ──────────────────────

async function unwrap<T>(res: Awaited<ReturnType<typeof fetchApi<T>>>): Promise<T> {
  if (res.error) throw new Error(res.error);
  return res.data as T;
}

export const pharmacyPortalModule = {
  getMyPharmacies: async () =>
    unwrap(await fetchApi<PharmacyUser[]>('/api/pharmacy/my-pharmacies')),

  getPendingPrescriptions: async (pharmacyId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery[]>(
      `/api/pharmacy/${pharmacyId}/prescriptions/pending`
    )),

  getPrescriptionDetails: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(`/api/pharmacy/prescriptions/${deliveryId}`)),

  acceptPrescription: async (
    deliveryId: string,
    fulfillmentData?: {
      medications?: Array<{ prescriptionMedicationId: string; dispensedQuantity?: number; substitutionNotes?: string }>;
      pharmacyNotes?: string;
    }
  ) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/accept`,
      { method: 'POST', body: JSON.stringify(fulfillmentData || {}) }
    )),

  rejectPrescription: async (deliveryId: string, rejectionReason: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/reject`,
      { method: 'POST', body: JSON.stringify({ rejectionReason }) }
    )),

  markPrepared: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/mark-prepared`,
      { method: 'POST' }
    )),

  markAsPrepared: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/mark-prepared`,
      { method: 'POST' }
    )),

  markCollected: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/mark-collected`,
      { method: 'POST' }
    )),

  markAsCollected: async (deliveryId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery>(
      `/api/pharmacy/prescriptions/${deliveryId}/mark-collected`,
      { method: 'POST' }
    )),

  getPrescriptionHistory: async (pharmacyId: string, status?: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionDelivery[]>(
      status
        ? `/api/pharmacy/${pharmacyId}/prescriptions/history?status=${status}`
        : `/api/pharmacy/${pharmacyId}/prescriptions/history`
    )),

  getStatistics: async (pharmacyId: string) =>
    unwrap(await fetchApi<PharmacyPrescriptionStats>(`/api/pharmacy/${pharmacyId}/statistics`)),

  listAssigned: async (_clinicId: string) =>
    unwrap(await fetchApi<Pharmacy[]>('/api/doctor/pharmacies?activeOnly=true')),
};

// ── Pharmacy Admin (super-admin / clinic-admin) ────────────────────────────

export const pharmacyAdminModule = {
  // Super-admin user management
  createPharmacyUser: (data: CreatePharmacyUserRequest) =>
    fetchApi<CreatePharmacyUserResponse>('/api/admin/pharmacy-users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPendingInvitations: () =>
    fetchApi<PharmacyInvitation[]>('/api/admin/pharmacy-users/invitations/pending'),

  resendInvitation: (invitationId: string) =>
    fetchApi<{ message: string }>(
      `/api/admin/pharmacy-users/invitations/${invitationId}/resend`,
      { method: 'POST' }
    ),

  cancelInvitation: (invitationId: string) =>
    fetchApi<void>(`/api/admin/pharmacy-users/invitations/${invitationId}`, {
      method: 'DELETE',
    }),

  // Pharmacy lifecycle
  registerPharmacy: (data: PharmacyRegistrationRequest) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy; status: string }>(
      '/api/public/pharmacy/register',
      { method: 'POST', body: JSON.stringify(data), skipAuth: true }
    ),

  acceptInvitation: (token: string, data: PharmacyRegistrationRequest) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy; status: string }>(
      `/api/public/pharmacy/accept-invitation?token=${token}`,
      { method: 'POST', body: JSON.stringify(data), skipAuth: true }
    ),

  invitePharmacy: (data: PharmacyInviteRequest) =>
    fetchApi<{ success: boolean; message: string; invitationId: string; email: string; expiresAt: string; pharmacyName: string }>(
      '/api/admin/pharmacy-invitations',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  getPendingApprovals: () =>
    fetchApi<Pharmacy[]>('/api/admin/pharmacies/pending'),

  approve: (pharmacyId: string, notes?: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/approve`,
      { method: 'POST', body: JSON.stringify({ notes }) }
    ),

  reject: (pharmacyId: string, reason: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  suspend: (pharmacyId: string, reason: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/suspend`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  reactivate: (pharmacyId: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/reactivate`,
      { method: 'POST' }
    ),

  getStatistics: () =>
    fetchApi<PharmacyStatistics>('/api/admin/pharmacies/statistics'),

  getAll: (activeOnly?: boolean) =>
    fetchApi<Pharmacy[]>(activeOnly ? '/api/pharmacies?activeOnly=true' : '/api/pharmacies'),

  resendActivation: (pharmacyId: string) =>
    fetchApi<{ success: boolean; message: string }>(
      `/api/admin/pharmacies/${pharmacyId}/resend-activation`,
      { method: 'POST' }
    ),

  sendInitialActivation: (pharmacyId: string) =>
    fetchApi<{ success: boolean; message: string }>(
      `/api/admin/pharmacies/${pharmacyId}/send-initial-activation`,
      { method: 'POST' }
    ),

  deactivate: (pharmacyId: string, reason: string) =>
    fetchApi<{ success: boolean; message: string; pharmacy: Pharmacy }>(
      `/api/admin/pharmacies/${pharmacyId}/deactivate`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  search: (searchRequest: PharmacySearchRequest) =>
    fetchApi<Pharmacy[]>('/api/pharmacies/search', {
      method: 'POST',
      body: JSON.stringify(searchRequest),
    }),

  // In-house pharmacy management (clinic admin)
  createInHousePharmacy: (data: CreateInHousePharmacyRequest) =>
    fetchApi<InHousePharmacyResponse>('/api/clinic/in-house-pharmacies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getClinicInHousePharmacies: () =>
    fetchApi<InHousePharmacyResponse[]>('/api/clinic/in-house-pharmacies'),

  getClinicActivePharmacies: () =>
    fetchApi<InHousePharmacyResponse[]>('/api/clinic/in-house-pharmacies?active=true'),

  getInHousePharmacy: (pharmacyId: string) =>
    fetchApi<InHousePharmacyResponse>(`/api/clinic/in-house-pharmacies/${pharmacyId}`),

  updateInHousePharmacy: (pharmacyId: string, data: Partial<CreateInHousePharmacyRequest>) =>
    fetchApi<InHousePharmacyResponse>(`/api/clinic/in-house-pharmacies/${pharmacyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deactivateInHousePharmacy: (pharmacyId: string) =>
    fetchApi<{ message: string }>(`/api/clinic/in-house-pharmacies/${pharmacyId}`, {
      method: 'DELETE',
    }),

  invitePharmacyStaff: (pharmacyId: string, data: InvitePharmacyStaffRequest) =>
    fetchApi<PharmacyStaffInvitationResponse>(
      `/api/clinic/in-house-pharmacies/${pharmacyId}/staff/invite`,
      { method: 'POST', body: JSON.stringify(data) }
    ),
};

// ── Pharmacy Medication Inventory ──────────────────────────────────────────

function throwOnError<T>(res: { data?: T; error?: string }): T {
  if (res.error) throw new Error(res.error);
  return res.data as T;
}

export const pharmacyMedicationsModule = {
  create: async (data: any) => throwOnError(await fetchApi<any>('/api/pharmacy-medications', { method: 'POST', body: JSON.stringify(data) })),
  list: async () => throwOnError(await fetchApi<any[]>('/api/pharmacy-medications')) ?? [],
  search: async (query: string) => throwOnError(await fetchApi<any[]>(`/api/pharmacy-medications/search?query=${encodeURIComponent(query)}`)) ?? [],
  getLowStock: async () => throwOnError(await fetchApi<any[]>('/api/pharmacy-medications/low-stock')) ?? [],
  getExpired: async () => throwOnError(await fetchApi<any[]>('/api/pharmacy-medications/expired')) ?? [],
  getExpiringSoon: async () => throwOnError(await fetchApi<any[]>('/api/pharmacy-medications/expiring-soon')) ?? [],
  getById: async (id: string) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}`)),
  update: async (id: string, data: any) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}`, { method: 'PUT', body: JSON.stringify(data) })),
  updateStock: async (id: string, newQuantity: number) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}/stock`, { method: 'PUT', body: JSON.stringify({ newQuantity }) })),
  updatePrice: async (id: string, newPrice: number) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}/price`, { method: 'PUT', body: JSON.stringify({ newPrice }) })),
  delete: async (id: string) => throwOnError(await fetchApi<any>(`/api/pharmacy-medications/${id}`, { method: 'DELETE' })),
};
